import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name.split(" ")[0]}!`);
      navigate(user.role === "admin" || user.role === "committee" ? "/admin" : "/member");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50" data-testid="login-page">
      <div className="hidden lg:block relative bg-[#0F2744]">
        <img src="https://customer-assets-39nsmqrw.emergentagent.net/job_rospa-wm-portal/artifacts/0sp0gjoh_ready-to-ride-on-a-Saturday-Medium.jpeg" alt="RoSPA West Midlands riders ready to ride" className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 flex flex-col justify-end p-12">
          <ShieldCheck className="h-12 w-12 text-amber-400 mb-4" />
          <h2 className="text-3xl font-bold text-white">Welcome back to the club</h2>
          <p className="mt-3 text-slate-300 max-w-md">Sign in to book events, track your training progress and access club resources.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 mb-8" data-testid="login-home-link"><ArrowLeft className="h-4 w-4" /> Back to site</Link>
          <h1 className="text-3xl font-extrabold text-[#0F2744]">Member login</h1>
          <p className="mt-2 text-slate-500">Sign in to your member area.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <Label className="mb-1.5 block">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label>Password</Label>
                <Link to="/forgot-password" className="text-sm font-semibold text-blue-600 hover:text-blue-700" data-testid="login-forgot-link">Forgot password?</Link>
              </div>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" />
            </div>
            {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full bg-[#0F2744] hover:bg-[#0B192C] h-12" data-testid="login-submit">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-slate-500 text-center">
            New here? <Link to="/register" className="font-semibold text-blue-600" data-testid="login-register-link">Create an account</Link> or <Link to="/join" className="font-semibold text-blue-600">apply to join</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
